import React, { useMemo, useState } from 'react'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../../Table/ActionableTable'
import { useTranslation } from 'react-i18next'
import { Card } from '@mui/material'
import { useNotificationContext } from '../../Context/NotificationContext'
import { Action } from '../../Table/RowActions'
import { Delete, Edit } from '@mui/icons-material'
import { useAuth } from '../../Routes/AuthProvider'
import { useNavigate } from 'react-router'
import { useTheme } from '@mui/material/styles'
import { notificationApi } from '../../../api/swagger'
import NotificationConfirmDeleteModal from '../../Modals/NotificationConfirmDeleteModal'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { OperationIds } from '../../../swagger/operationIdEnum'
import { dateToDashString } from '../../../utils/dateUtility'

interface NotificationTableProps {
  /**
   * A callback method to update the notification history when a change occurs
   * @type function
   */
  triggerRefresh?: () => void
}

export const NotificationsTable: React.FC<NotificationTableProps> = ({
  triggerRefresh,
}) => {
  const { t } = useTranslation()
  const {
    notifications,
    pagination,
    updateSelectedNotificationKey,
    selectedNotificationKey,
  } = useNotificationContext()
  const { permissionAbility } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const { handleError } = useAppSnackbar()

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const defaultDeleteNotificationErrorMsg = t(
    'Admin.Notifications.Table.DeleteNotification.Error',
    'An error occurred deleting the notification'
  )
  const deleteNotification = async () => {
    try {
      if (selectedNotificationKey) {
        await notificationApi.deleteNotification({
          notificationKey: selectedNotificationKey,
        })
      }
      setShowConfirmDeleteModal(false)
      triggerRefresh?.()
    } catch (e) {
      await handleError(e, defaultDeleteNotificationErrorMsg)
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: deleteNotification,
    loadingId: OperationIds.DeleteNotification,
    callback: triggerRefresh,
  })

  const actionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      fieldName: 'notificationKey',
      columnHeaderName: t('Admin.Notifications.Table.Header.Id', 'Id'),
    },
    {
      fieldName: 'name',
      columnHeaderName: t('Admin.Notifications.Table.Header.Name', 'Name'),
    },
    {
      fieldName: 'startDate',
      columnHeaderName: t(
        'Admin.Notifications.Table.Header.StartDate',
        'Start Date'
      ),
    },
    {
      fieldName: 'startTime',
      columnHeaderName: t(
        'Admin.Notifications.Table.Header.StartTime',
        'Start Time (GMT)'
      ),
    },
    {
      fieldName: 'endDate',
      columnHeaderName: t(
        'Admin.Notifications.Table.Header.EndDate',
        'End Date'
      ),
    },
    {
      fieldName: 'endTime',
      columnHeaderName: t(
        'Admin.Notifications.Table.Header.EndTime',
        'End Time (GMT)'
      ),
    },
  ]

  const rows = useMemo(() => {
    return notifications.map(
      ({
        notificationKey,
        name,
        startDate: initialStartDate,
        endDate: initialEndDate,
      }) => {
        const [, startTime] = initialStartDate.toISOString().split('T')
        const [, endTime] = initialEndDate.toISOString().split('T')
        return {
          notificationKey,
          name,
          startDate: dateToDashString(initialStartDate),
          startTime,
          endDate: dateToDashString(initialEndDate),
          endTime,
        }
      }
    )
  }, [notifications])

  const rowActions: Action<(typeof rows)[0]>[] = useMemo(() => {
    const editAction: Action<(typeof rows)[0]> = {
      actionName: t(
        'Admin.Notifications.NotificationsTable.Action.Edit',
        'Edit'
      ),
      actionFunction: (row) => {
        updateSelectedNotificationKey(row.notificationKey)
        navigate(
          `/admin/notifications/notification-details/${row.notificationKey}`
        )
      },
      actionKey: 'editNotification',
      actionIcon: <Edit style={{ minWidth: theme.spacing(4) }} />,
      hide: () => !permissionAbility.can('edit', 'Notification'),
    }
    const deleteAction: Action<(typeof rows)[0]> = {
      actionName: t(
        'Admin.Notifications.NotificationsTable.Action.Delete',
        'Delete'
      ),
      actionFunction: (row) => {
        updateSelectedNotificationKey(row.notificationKey)
        setShowConfirmDeleteModal(true)
      },
      actionKey: 'deleteNotification',
      actionIcon: <Delete style={{ minWidth: theme.spacing(4) }} />,
      hide: () => !permissionAbility.can('delete', 'Notification'),
    }
    return [editAction, deleteAction]
  }, [permissionAbility, t, navigate, theme, updateSelectedNotificationKey])

  return (
    <Card>
      <NotificationConfirmDeleteModal
        isOpen={showConfirmDeleteModal}
        onConfirm={triggerFetch}
        onCancel={() => setShowConfirmDeleteModal(false)}
        notificationName={
          notifications.find(
            (noti) => noti.notificationKey === selectedNotificationKey
          )?.name || ''
        }
      />
      <ActionableTable
        checkboxSelection={notifications.length > 0}
        columns={actionableTableColumnsHeader}
        rows={rows}
        rowActions={rowActions}
        noResultsMessage={t(
          'Admin.Notifications.NotificationsTable.NoResultsFound',
          'No Results Found'
        )}
        pagination={pagination}
      />
    </Card>
  )
}
